<template>
  <div class="anamnesis-page">
    <div class="anamnesis-signature" :class="{ 'is-signed': isSigned }">
      <template v-if="signature">
        <signature-banner
          :signature="signature"
          :discarding="discardingSignature"
          :can-discard="canSign"
          :can-retry="canSign"
          @retry="sign"
          @discard="$emit('discard-signature')"
        >
          <st-button-report
            v-if="isSigned"
            slot="actions"
            type="pdf"
            button-class="btn-sm btn-primary"
            label="Imprimir anamnese assinada"
            :icon="['fal', 'file-signature']"
            :options="null"
            :route="printLink"
          ></st-button-report>
        </signature-banner>
        <div class="anamnesis-signed" v-if="isSigned">
          <div class="anamnesis-signed-sheet">
            <div class="anamnesis-signed-heading">
              <div class="anamnesis-signed-heading-icon">
                <fa-icon :icon="['fal', 'file-medical-alt']" />
              </div>
              <div class="anamnesis-signed-heading-text">
                <div class="anamnesis-signed-title">Anamnese / evolução</div>
                <div class="anamnesis-signed-subtitle">{{ signedSubtitle }}</div>
              </div>
              <div class="anamnesis-signed-date" v-if="data.startDate">
                <fa-icon :icon="['fal', 'calendar-alt']" />
                <span>{{ data.startDate | date }}</span>
              </div>
            </div>
            <div class="anamnesis-signed-body">
              <div v-if="hasSignedContent">
                <ophthalmology-item
                  v-if="signedItem.anamnesis.templateRenderer === 'ophthalmology'"
                  :item="signedItem"
                />
                <odontogram-item
                  v-else-if="signedItem.anamnesis.templateRenderer === 'odontogram'"
                  :item="signedItem"
                />
                <default-item v-else :item="signedItem" />
              </div>
              <div class="anamnesis-signed-none" v-else>Anamnese sem conteúdo</div>
            </div>
          </div>
          <div class="anamnesis-signed-sheet" v-if="signedMeasures.length > 0">
            <div class="anamnesis-signed-heading">
              <div class="anamnesis-signed-heading-icon">
                <fa-icon :icon="['fal', 'heartbeat']" />
              </div>
              <div class="anamnesis-signed-heading-text">
                <div class="anamnesis-signed-title">Antropometria e sinais vitais</div>
                <div class="anamnesis-signed-subtitle">
                  {{ signedMeasures.length === 1
                    ? '1 medida registrada'
                    : `${signedMeasures.length} medidas registradas` }}
                </div>
              </div>
            </div>
            <div class="anamnesis-signed-pills">
              <span
                class="anamnesis-signed-pill"
                v-for="measure in signedMeasures"
                :key="measure.label"
              >
                <span class="anamnesis-signed-pill-key">{{ measure.label }}</span>
                {{ measure.value }}
              </span>
            </div>
          </div>
          <div class="anamnesis-signed-sheet" v-if="form.cids.length > 0">
            <div class="anamnesis-signed-heading">
              <div class="anamnesis-signed-heading-icon">
                <fa-icon :icon="['fal', 'stethoscope']" />
              </div>
              <div class="anamnesis-signed-heading-text">
                <div class="anamnesis-signed-title">CID</div>
                <div class="anamnesis-signed-subtitle">
                  {{ form.cids.length === 1
                    ? '1 diagnóstico informado'
                    : `${form.cids.length} diagnósticos informados` }}
                </div>
              </div>
            </div>
            <div class="anamnesis-signed-list">
              <div
                class="anamnesis-signed-cid"
                v-for="cid in form.cids"
                :key="cid.code"
              >
                <span class="anamnesis-signed-pill is-code">{{ cid.code }}</span>
                <span class="anamnesis-signed-cid-name">{{ cid.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="card-detail" v-if="appointment.startDate">
      <div class="card-detail-title mb-1">Data e hora do agendamento</div>
      <div class="card-detail-data">
        <span class="mr-1">{{ appointment.startDate | date('DD/MM/YYYY HH:mm') }}</span>
        <span class="text-small">({{ appointment.slot ? 'Encaixe' : 'Horário marcado' }})</span>
      </div>
      <template v-if="expenses && expenses.length > 0">
        <div class="card-detail-title mt-2 mb-1">Serviços agendados</div>
        <div class="card-detail-data"
             v-for="(expense, i) in expenses" :key="i">
          {{ expense.expense.code | tuss }} - {{ expense.expense.name }}
        </div>
      </template>
    </div>

    <div class="anamnesis-actions">
      <div class="anamnesis-actions-templates">
        <div class="btn-group" v-if="!isSigned">
          <button
            type="button"
            class="btn"
            :class="item.id === form.anamnesis.templateId ? 'btn-primary' : 'btn-neutral'"
            :disabled="isSignatureLocked"
            @click="onClickTemplate(item.id)"
            v-for="(item, i) in templates"
            :key="i"
          >{{ item.name }}
          </button>
        </div>
      </div>
      <div class="anamnesis-actions-end">
        <div class="input-group">
          <select
            class="form-select"
            v-model="form.tag.id"
          >
            <option value="">Vincular Tag ao atendimento</option>
            <option v-for="item in tags" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
          <span class="input-group-addon text-info">
            <div class="popover popover-left">
              <fa-icon :icon="['fal', 'info-circle']"/>
              <div class="popover-container">
                <div class="card">
                  <div class="card-header">
                    <h6>Cadastro de Tags</h6>
                  </div>
                  <div class="card-body text-dark">
                    Para cadastrar ou editar Tags clique no<br>
                    seu nome no menu superior, configurações<br>
                    gerais e depois Tags.
                  </div>
                </div>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>

    <div class="divider" />

    <div v-if="ready">
      <fieldset
        v-if="!isSigned"
        class="anamnesis-fieldset"
        :class="{ 'is-locked': isSignatureLocked }"
        :disabled="isSignatureLocked"
      >
        <component
          v-if="hasCustomRenderer"
          :is="customComponent"
          :data="form.anamnesis"
          :history="history"
          :id="data.id"
          :cids="form.cids"
          :disabled="isSignatureLocked"
          @change="customFormChange"
          @add-cid="setCid"
          @remove-cid="removeCid"
        />

        <template v-else>
          <div class="hover-link text-bold form-group"
               :class="anthropometry.show ? 'text-warning' : 'text-primary'"
               @click="anthropometry.show = !anthropometry.show">
            <fa-icon :icon="['fal', anthropometry.show ? 'chevron-up' : 'chevron-down']"/>
            Antropometria e sinais vitais
          </div>

          <template v-if="anthropometry.show">
            <div class="columns">
              <div class="column col-3 col-md-6 col-sm-12 form-group">
                <label for="weight" class="form-label">Peso</label>
                <div class="input-group">
                  <dx-input-number
                    id="weight"
                    class="form-input"
                    v-model="form.anthropometry.weight"
                    :precision="3"
                    maxlength="7"
                  />
                  <span class="input-group-addon text-gray">kg</span>
                </div>
              </div>
              <div class="column col-3 col-md-6 col-sm-12 form-group">
                <label for="height" class="form-label">Altura</label>
                <div class="input-group">
                  <dx-input-number
                    id="height"
                    class="form-input"
                    v-model="form.anthropometry.height"
                    :precision="1"
                    maxlength="5"
                  />
                  <span class="input-group-addon text-gray">cm</span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12 form-group">
                <label class="form-label">IMC</label>
                <div class="input-group">
                  <span class="form-input text-right text-bold bg-gray">
                    {{ imc | number(2) }}
                  </span>
                  <span class="input-group-addon text-info tooltip c-hand"
                        data-tooltip="Curvas de crescimento" @click="openGrowthChart">
                  <fa-icon :icon="['fal', 'chart-line']" />
                </span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12 form-group">
                <label for="cephalic-perimeter" class="form-label">Per. cefálico</label>
                <div class="input-group">
                  <dx-input-number
                    id="cephalic-perimeter"
                    class="form-input"
                    v-model="form.anthropometry.cephalicPerimeter"
                    :precision="1"
                    maxlength="5"
                  />
                  <span class="input-group-addon text-gray">cm</span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12 form-group">
                <label for="abdominal-circumference" class="form-label">C. abdominal</label>
                <div class="input-group">
                  <dx-input-number
                    id="abdominal-circumference"
                    class="form-input"
                    v-model="form.anthropometry.abdominalCircumference"
                    :precision="1"
                    maxlength="5"
                  />
                  <span class="input-group-addon text-gray">cm</span>
                </div>
              </div>
              <div class="column col-3 col-md-6 col-sm-12">
                <label for="blood-pressure" class="form-label">Pressão Arterial</label>
                <div class="input-group">
                  <input
                    type="text"
                    id="blood-pressure"
                    class="form-input text-right"
                    v-model="form.vitalSigns.bloodPressure"
                    maxlength="7"
                  >
                  <span class="input-group-addon text-gray">mm/Hg</span>
                </div>
              </div>
              <div class="column col-3 col-md-6 col-sm-12">
                <label for="respiratory-frequency" class="form-label">F. Respiratória</label>
                <div class="input-group">
                  <dx-input-number
                    id="respiratory-frequency"
                    class="form-input"
                    v-model="form.vitalSigns.respiratoryFrequency"
                    :precision="0"
                    maxlength="3"
                  />
                  <span class="input-group-addon text-gray">mpm</span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12">
                <label for="heart-rate" class="form-label">F. Cardíaca</label>
                <div class="input-group">
                  <dx-input-number
                    id="heart-rate"
                    class="form-input"
                    v-model="form.vitalSigns.heartRate"
                    :precision="0"
                    maxlength="3"
                  />
                  <span class="input-group-addon text-gray">bpm</span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12">
                <label for="temperature" class="form-label">Temperatura</label>
                <div class="input-group">
                  <dx-input-number
                    id="temperature"
                    class="form-input"
                    v-model="form.vitalSigns.temperature"
                    :precision="2"
                    maxlength="5"
                  />
                  <span class="input-group-addon text-gray">ºC</span>
                </div>
              </div>
              <div class="column col-2 col-md-6 col-sm-12">
                <label for="saturation" class="form-label">Saturação O&sup2;</label>
                <div class="input-group">
                  <dx-input-number
                    id="saturation"
                    class="form-input"
                    v-model="form.vitalSigns.saturation"
                    :precision="0"
                    maxlength="3"
                  />
                  <span class="input-group-addon text-gray">%</span>
                </div>
              </div>
            </div>
            <div class="divider" data-content="" />
          </template>

          <div class="columns">
            <div class="column col-6 col-sm-12 form-group mb-2">
              <label
                class="form-label"
                :class="cidIsRequired ? 'text-error text-bold' : ''"
              >CID</label>
              <dx-autocomplete
                id="cid"
                v-model="cid"
                :source="findCid"
                @select="setCid"
                :debounce="800"
                placeholder="Pesquisar CID"
              >
                <template v-slot="{ item }">
                  <fa-icon class="text-primary" :icon="['fal', 'heartbeat']" />
                  {{ item.code }} - {{ item.name }}
                </template>
                <template v-slot:action>
                  <button
                    type="button"
                    class="btn btn-neutral btn-action input-group-btn btn-icon"
                  >
                    <fa-icon :icon="['fal', 'search']" />
                  </button>
                </template>
              </dx-autocomplete>
              <template v-if="cidIsRequired && !cid">
                <div class="form-input-hint text-error">Campo obrigatório</div>
              </template>
            </div>
            <div class="column col-sm-12 form-group mb-2" v-if="canSuggestCid">
              <label class="form-label" aria-hidden="true">&nbsp;</label>
              <button
                type="button"
                class="btn btn-info-outline btn-icon btn-icon-left tooltip"
                :class="{ loading: cidSuggesting }"
                :disabled="cidSuggesting || !hasAnamnesisText"
                :data-tooltip="cidSuggestTooltip"
                @click="suggestCid"
              >
                <fa-icon :icon="['fal', 'sparkles']" /> Sugerir CID
              </button>
            </div>
          </div>
          <div class="chip bg-secondary mb-2" v-for="(cid, c) in form.cids" :key="cid.code">
            {{ cid.code }} - {{ cid.name.substring(0, 40) }}{{ cid.name.length > 40 ? '...' : '' }}
            <fa-icon class="c-hand ml-2" :icon="['fal', 'times']"
                     v-if="!isSignatureLocked"
                     @click="removeCid(c)"/>
          </div>

          <cid-suggestions
            v-if="canSuggestCid"
            ref="cidSuggestions"
            :medical-record-id="data.id"
            :text="anamnesisText"
            :cids="form.cids"
            @select="setCid"
            @loading="cidSuggesting = $event"
          />

          <div class="input-group form-group">
            <select
              class="form-select"
              v-model="modelId"
              @change="getModel"
              :disabled="loading"
            >
              <option value="">[Meus modelos]</option>
              <option v-for="(item, i) in models" :value="item.id" :key="i">
                {{ item.name }}
              </option>
            </select>
            <button class="btn btn-neutral input-group-btn"
                    @click="openEditModel">
              Gerenciar
            </button>
          </div>
          <div v-for="input in form.anamnesis.inputs" :key="input.name">
            <question
              class="mb-2"
              v-model="input.value"
              :label="input.label"
              :type="input.type"
              :options="input.options"
              :params="input.params"
              :disabled="isSignatureLocked"
            />
            <button
              type="button"
              class="btn btn-sm btn-info-outline mb-2 tooltip tooltip-right"
              @click="getLastValue(input.name)"
              data-tooltip="Preenche o campo com dados do último atendimento"
            >Copiar do último atendimento
            </button>
          </div>
        </template>
      </fieldset>
    </div>
    <div v-if="!triage">
      <div class="anamnesis-page-footer" v-if="!isSigned">
        <button class="btn btn-gray btn-icon-left"
                :class="{loading: printing}"
                @click="print" :disabled="printing || pending || isSignatureRunning">
          <fa-icon :icon="['fal', 'print']"/>
          <span v-if="pending">Salvando...</span>
          <span v-else>Imprimir documento</span>
        </button>
        <span
          class="anamnesis-sign-action ml-2"
          :class="{ 'tooltip tooltip-top': !!signBlockedReason }"
          :data-tooltip="signBlockedReason"
          v-if="canSign && !signature"
        >
          <button
            type="button"
            class="btn btn-gray btn-icon btn-icon-left"
            :disabled="!!signBlockedReason"
            @click="sign"
          ><fa-icon :icon="['fal', 'signature']" /> Assinar</button>
        </span>
      </div>
    </div>
    <st-feedback
      context="anamnesis"
      label="Está faltando algo na anamnese ou tem uma sugestão? Fale com a gente"
      missing-label="Está faltando um campo ou modelo"
      placeholder="Informe o que está faltando ou o que podemos melhorar na anamnese..."
    />
    <modal-edit
      v-if="editModels.show"
      :show="editModels.show"
      @close="closeEditModel"
    />
    <modal-growth
      v-if="growthCurves.show"
      :patient-id="patient.id"
      :show="growthCurves.show"
      @close="closeGrowthModel"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import formMixin from 'src/mixins/form';
import ai from '@/mixins/ai';
import moment from 'moment';
import DateOld from '@/filters/date-old';
import Cpf from '@/filters/cpf';
import Phone from '@/filters/phone';
import localforage from 'localforage';
import * as measures from 'src/data/medical-record-measures';
import { SIGNED, isLocked, isRunning } from '@/data/digital-signature';
import SignatureBanner from '@/components/digital-signature/SignatureBanner.vue';
import Question from '../Question.vue';
import DefaultItem from '../item/DefaultItem.vue';
import OphthalmologyItem from '../item/OphthalmologyItem.vue';
import OdontogramItem from '../item/OdontogramItem.vue';
import ModalEdit from '../../../template/components/anamnesis/modals/Main.vue';
import ModalGrowth from '../../../patient/modals/GrowthCurves.vue';
import CidSuggestions from '../assistant/CidSuggestions.vue';
import OphthalmologyForm from './OphthalmologyForm.vue';
import OdontogramForm from './OdontogramForm.vue';
import { useInputs } from '../helpers/inputs';

const CID_SUGGEST_MIN_LENGTH = 80;

export default {
  components: {
    CidSuggestions,
    ModalEdit,
    Question,
    ModalGrowth,
    OphthalmologyForm,
    OdontogramForm,
    SignatureBanner,
    DefaultItem,
    OphthalmologyItem,
    OdontogramItem,
  },
  props: {
    appointment: {
      type: Object,
      required: true,
    },
    history: {
      type: Array,
      default: () => [],
    },
    expenses: {
      type: Array,
    },
    patient: {
      id: {
        type: String,
      },
      name: {
        type: String,
      },
      birthDate: {
        type: Date,
      },
      gender: {
        type: String,
      },
      phone: {
        type: String,
      },
      cellphone: {
        type: String,
      },
      identity: {
        type: {
          type: String,
        },
        value: {
          type: String,
        },
      },
      addresses: {
        type: Array,
      },
      allergy: {
        type: Boolean,
      },
    },
    data: {
      id: {
        type: String,
      },
      tag: {
        id: {
          type: String,
        },
        name: {
          type: String,
        },
      },
      anthropometry: {
        height: {
          type: Number,
        },
        weight: {
          type: Number,
        },
        cephalicPerimeter: {
          type: Number,
        },
        abdominalCircumference: {
          type: Number,
        },
      },
      vitalSigns: {
        bloodPressure: {
          type: String,
        },
        respiratoryFrequency: {
          type: Number,
        },
        heartRate: {
          type: Number,
        },
        temperature: {
          type: Number,
        },
        saturation: {
          type: Number,
        },
      },
      anamnesis: {
        templateId: {
          type: String,
        },
        inputs: {
          type: Array,
        },
      },
      cids: {
        type: Array,
      },
    },
    pending: {
      type: Boolean,
    },
    printing: {
      type: Boolean,
    },
    signature: {
      type: Object,
      default: null,
    },
    canSign: {
      type: Boolean,
      default: false,
    },
    discardingSignature: {
      type: Boolean,
      default: false,
    },
  },
  inject: ['triage'],
  mixins: [
    formMixin,
    ai,
  ],
  data() {
    return {
      form: this.data,
      templates: [],
      models: [],
      tags: [],
      modelId: '',
      loading: false,
      cid: null,
      cidSuggesting: false,
      cidSuggestEnabled: false,
      editModels: {
        show: false,
      },
      growthCurves: {
        show: false,
      },
      anthropometry: {
        show: false,
      },
      templateRenderer: null,
      ready: false,
    };
  },
  watch: {
    'data.id': {
      handler: 'loadCidSuggestStatus',
      immediate: true,
    },
  },
  async mounted() {
    this.loadTags();

    await this.loadTemplates();
    await this.loadModels();
    if (!this.form.anamnesis.templateId) {
      const templateId = await this.getProfessionalSettings();
      if (templateId) {
        this.form.anamnesis.templateId = templateId;
      } else {
        this.form.anamnesis.templateId = this.templates.length > 0 ? this.templates[0].id : '';
      }
      this.changeType(false);
    } else {
      this.changeType(true);
    }
    this.ready = true;
  },
  computed: {
    ...mapGetters({
      branchSettings: 'branchSettings',
    }),
    ...mapState({
      user: state => state.auth.user,
    }),
    isSignatureLocked() {
      return isLocked(this.signature);
    },
    isSignatureRunning() {
      return isRunning(this.signature);
    },
    isSigned() {
      return !!this.signature && this.signature.status === SIGNED;
    },
    signBlockedReason() {
      if (!this.form.anamnesis.templateId) {
        return 'Selecione um modelo de anamnese para assinar';
      }
      if (this.pending) {
        return 'Aguarde o salvamento da anamnese';
      }
      return null;
    },
    printLink() {
      return `/medical-records/${this.data.id}/print`;
    },
    signedItem() {
      const { inputs, templateRenderer } = this.form.anamnesis;
      const values = Array.isArray(inputs)
        ? inputs.reduce((acc, input) => {
          acc[input.name] = input.value;
          return acc;
        }, {})
        : { ...(inputs || {}) };

      return {
        tag: null,
        anamnesis: {
          templateId: this.form.anamnesis.templateId,
          templateRenderer: templateRenderer || this.templateRenderer,
          inputs: values,
        },
        anthropometry: {},
        vitalSigns: {},
        cids: [],
      };
    },
    hasSignedContent() {
      const { inputs } = this.signedItem.anamnesis;
      return Object.keys(inputs).some((name) => {
        const value = inputs[name];
        return typeof value === 'string'
          ? !!value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
          : !!value;
      });
    },
    signedTemplateName() {
      const template = this.templates.find(item => item.id === this.form.anamnesis.templateId);
      return template ? template.name : '';
    },
    signedSubtitle() {
      return [
        this.signedTemplateName ? `Modelo ${this.signedTemplateName}` : null,
        'Conteúdo assinado digitalmente, disponível apenas para leitura',
      ].filter(v => !!v).join(' · ');
    },
    signedMeasures() {
      return measures.filled([
        ...measures.anthropometry(this.form.anthropometry),
        ...measures.vitalSigns(this.form.vitalSigns),
      ]);
    },
    canSuggestCid() {
      return this.aiEnabled && this.cidSuggestEnabled && !!this.data.id && !this.triage;
    },
    hasAnamnesisText() {
      return this.anamnesisLength >= CID_SUGGEST_MIN_LENGTH;
    },
    anamnesisLength() {
      return this.anamnesisText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length;
    },
    cidSuggestTooltip() {
      return this.hasAnamnesisText
        ? 'Sugira CID a partir do que está escrito na anamnese'
        : `Escreva mais na anamnese para usar a sugestão (${
          this.anamnesisLength}/${CID_SUGGEST_MIN_LENGTH} caracteres)`;
    },
    anamnesisText() {
      const inputs = this.form.anamnesis && this.form.anamnesis.inputs;

      if (!Array.isArray(inputs)) {
        return '';
      }

      return inputs
        .filter(input => ['editor', 'text'].includes(input.type))
        .map(input => (typeof input.value === 'string' ? input.value : ''))
        .filter(Boolean)
        .join('\n');
    },
    imc() {
      const height = this.decimalToApi(this.form.anthropometry.height);
      const weight = this.decimalToApi(this.form.anthropometry.weight);
      const result = (weight / (height * height)) * 10000;
      return result < 14 || result > 55 ? 0 : result;
    },
    hasCustomRenderer() {
      return this.templateRenderer !== null;
    },
    customComponent() {
      return this.hasCustomRenderer ? `${this.templateRenderer}-form` : 'div';
    },
    cidIsRequired() {
      return this.branchSettings
        && this.branchSettings['medicalRecord.cidRequired']
        && this.form.cids.length === 0;
    },
  },
  methods: {
    insertHistoryValue({ inputs }) {
      Object.keys(inputs)
        .forEach((key) => {
          const formInput = this.form.anamnesis.inputs.find(item => item.name === key);
          if (formInput && !formInput.value.includes(inputs[key])) {
            formInput.value += inputs[key];
          }
        });
    },
    getLastValue(selector) {
      const found = this.history
        .find(({ anamnesis }) => {
          const inputHelper = useInputs(anamnesis.inputs || {});
          return inputHelper.hasFilledWith(selector);
        });

      if (found) {
        const inputHelper = useInputs(found.anamnesis.inputs);
        const inputs = inputHelper.getWith(selector);
        this.insertHistoryValue({ inputs });
      }
    },
    customFormChange({
      name,
      value,
    }) {
      if (this.isSignatureLocked) {
        return;
      }

      if (name === 'odontograma') {
        this.form.anamnesis.inputs = value;
      } else {
        if (!Array.isArray(this.form.anamnesis.inputs)) {
          this.form.anamnesis.inputs = [];
        }

        const input = this.form.anamnesis.inputs.find(item => item.name === name);

        if (input) {
          input.value = value;
        } else if (name === 'content') {
          this.form.anamnesis.inputs.push({
            name: 'content',
            label: 'Anotações gerais',
            type: 'editor',
            value,
          });
        }
      }
    },
    openTemplate(templateId) {
      if (
        this.form.anamnesis
        && this.form.anamnesis.inputs
        && this.form.anamnesis.inputs.length > 0
        && this.form.anamnesis.inputs[0].value
      ) {
        this.$dialog.show('', {
          html:
            '<div class="text-center">'
            + '<h5 class="text-center">Atenção!</h5>'
            + '<div>Ao trocar o formato de atendimento, os dados informados</div>'
            + '<div>serão perdidos. Deseja continuar?</div>'
            + '</div>',
          buttons: [
            {
              label: 'Não',
              classes: '',
            }, {
              label: 'Sim',
              classes: 'btn-primary btn-error ml-2',
              click: (close) => {
                this.form.anamnesis.templateId = templateId;
                this.changeType(false);
                close();
              },
            },
          ],
        });
      } else {
        this.form.anamnesis.templateId = templateId;
        this.changeType(false);
      }
    },
    changeType(firstLoad) {
      if (this.form.anamnesis.templateId) {
        this.setProfessionalSettings(this.form.anamnesis.templateId);
        const template = this.templates.find(item => (
          item.id === this.form.anamnesis.templateId
        ));
        this.templateRenderer = template.templateRenderer || null;
        this.form.anamnesis.templateRenderer = this.templateRenderer;
        if (!firstLoad || template.id !== this.form.anamnesis.templateId) {
          this.setTemplateInputs(template.inputs);
        }
      }
    },
    setProfessionalSettings(templateId) {
      localforage.setItem(`${this.user.id}:anamnesis_types`, templateId);
    },
    getProfessionalSettings() {
      return localforage.getItem(`${this.user.id}:anamnesis_types`);
    },
    setTemplateInputs(inputs) {
      this.form.anamnesis.inputs = inputs.map(item => ({
        ...item,
        value: item.value || '',
      }));
    },
    loadCidSuggestStatus() {
      if (!this.aiEnabled || !this.data.id || this.triage) {
        this.cidSuggestEnabled = false;
        return Promise.resolve();
      }

      return this.$httpX
        .get(`/medical-records/${this.data.id}/assistant/cid-suggest`)
        .then(({ data }) => {
          this.cidSuggestEnabled = !!data.enabled;
        })
        .catch(() => {
          this.cidSuggestEnabled = false;
        });
    },
    loadTags() {
      const params = {
        type: 'medical_record',
        active: true,
      };

      return this.$http.get('/tags', { params })
        .then(({ data }) => {
          this.tags = data.items;
        });
    },
    loadTemplates() {
      const params = {
        limit: 0,
        active: true,
        type: 'anamnesis',
      };
      return this.$httpX.get('/templates', { params })
        .then(({ data }) => {
          this.templates = data.items;
        });
    },
    loadModels() {
      const params = {
        active: true,
        limit: 0,
        professionalId: this.user.id,
      };
      this.$httpX.get('/anamnesis-templates', { params })
        .then(({ data }) => {
          this.models = data.items;
        })
        .catch(this.$toast.error);
    },
    getModel() {
      if (!this.modelId) {
        return;
      }

      if (
        this.form.anamnesis
        && this.form.anamnesis.inputs
        && this.form.anamnesis.inputs.length > 0
        && this.form.anamnesis.inputs[0].value
      ) {
        this.$dialog.show('', {
          html:
            '<div class="text-center">'
            + '<h5 class="text-center">Atenção!</h5>'
            + '<div>O modelo selecionado irá substituir as informações</div>'
            + '<div>digitadas. Deseja continuar?</div>'
            + '</div>',
          buttons: [
            {
              label: 'Não',
              classes: '',
              click: (close) => {
                this.modelId = '';
                close();
              },
            }, {
              label: 'Sim',
              classes: 'btn-primary btn-error ml-2',
              click: (close) => {
                this.setAnamnesis();
                close();
              },
            },
          ],
        });
      } else {
        this.setAnamnesis();
      }
    },
    setAnamnesis() {
      this.loading = true;
      this.$httpX.get(`/anamnesis-templates/${this.modelId}`)
        .then(({ data }) => {
          if (data && data.data) {
            const address = this.patient.addresses
            && this.patient.addresses.length > 0
              ? this.patient.addresses[0] : null;

            if (
              this.form.anamnesis
              && this.form.anamnesis.inputs
              && this.form.anamnesis.inputs.length > 0
              && this.form.anamnesis.inputs[0]
            ) {
              this.form.anamnesis.inputs[0].value = data.data
                .replace(/\[date]/g, moment()
                  .format('DD/MM/YYYY'))
                .replace(/\[hour]/g, moment()
                  .format('HH:mm'))
                .replace(/\[cid]/g, '')
                .replace(/\[patient_name]/g, this.patient.name)
                .replace(/\[patient_gender]/g,
                  this.patient.gender === 'male'
                    ? 'Masculino'
                    : 'feminino')
                .replace(/\[patient_birth_date]/g, moment(this.patient.birthDate)
                  .format('DD/MM/YYYY'))
                .replace(/\[patient_age]/g,
                  DateOld(this.patient.birthDate, true))
                .replace(/\[patient_cpf]/g,
                  this.patient.identity && this.patient.identity.value
                    ? Cpf(this.patient.identity.value)
                    : null)
                .replace(/\[patient_phone]/g, Phone(this.patient.cellphone))
                .replace(/\[patient_address]/g, this.formatAddress(address));
            }
          }
        })
        .catch(this.$toast.error)
        .then(() => {
          this.loading = false;
          this.modelId = '';
        });
    },
    openEditModel() {
      this.editModels.show = true;
    },
    closeEditModel() {
      this.loadModels();
      this.editModels.show = false;
    },
    closeGrowthModel() {
      this.growthCurves.show = false;
    },
    openGrowthChart() {
      this.growthCurves.show = true;
    },
    onClickTemplate(templateId) {
      if (this.isSignatureLocked) {
        return;
      }
      this.openTemplate(templateId);
    },
    formatAddress(item) {
      if (item) {
        return `${
          item.addressLine1
        } ${
          item.addressLine2
        }, ${
          item.neighborhood
        } - ${
          item.city
        }-${
          item.state
        }`;
      }
      return '';
    },
    removeCid(idx) {
      if (this.isSignatureLocked) {
        return;
      }
      this.form.cids.splice(idx, 1);
    },
    findCid(search) {
      return this.$httpX.get(`/services/cids?search=${search}`)
        .then(({ data }) => data.items);
    },
    hasCid(code) {
      return this.form.cids.some(cid => cid.code === code);
    },
    suggestCid() {
      const suggestions = this.$refs.cidSuggestions;

      if (!suggestions) {
        return;
      }

      suggestions.load();
    },
    setCid(data) {
      this.cid = null;

      if (this.isSignatureLocked || !data || !data.code) {
        return;
      }

      if (this.hasCid(data.code)) {
        this.$toast.show(`CID ${data.code} já está no atendimento.`, { type: 'warning' });
        return;
      }

      this.form.cids.push({
        code: data.code,
        name: data.name,
      });
    },
    print() {
      this.$emit('print');
    },
    sign() {
      if (!this.canSign || this.signBlockedReason) {
        return;
      }
      this.$emit('sign');
    },
  },
};
</script>

<style lang="scss">
@import "../../../../assets/scss/variables";
$anamnesis-signed-inset: $layout-spacing-lg + .2rem - $border-width;

.anamnesis-page {
  .card-detail {
    background-color: lighten($gray-color-light, 5%);
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing;
    padding: $layout-spacing;

    .card-detail-title {
      font-weight: bold;
      margin-bottom: $layout-spacing;
    }
  }

  .document-editor-header {
    position: sticky;
    top: 80px;
    background: #FFF;
    padding-top: $layout-spacing;
  }

  .document-editor-body {
    .ck.ck-content.ck-editor__editable {
      min-height: 15rem;
    }
  }

  .anamnesis-page-footer {
    align-items: center;
    display: flex;
    justify-content: center;
    padding: $layout-spacing-lg;
    text-align: center;

    button {
      min-width: 7rem;
    }
  }

  .anamnesis-actions {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
  }

  .anamnesis-actions-end {
    align-items: center;
    display: flex;
    gap: $layout-spacing-sm;
    margin-left: auto;
  }

  .anamnesis-sign-action {
    display: inline-flex;
  }

  .anamnesis-fieldset {
    border: 0;
    margin: 0;
    min-width: 0;
    padding: 0;
    &.is-locked .chip {
      opacity: .7;
    }
  }

  .anamnesis-signature {
    .digital-signature-banner {
      flex-wrap: wrap;
    }
    .banner-content {
      flex: 1 1 12rem;
    }
    .banner-actions {
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-left: auto;
    }
    &.is-signed {
      background-color: lighten($gray-color, 24%);
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      margin-bottom: $layout-spacing-lg;
      padding: $layout-spacing-lg;
      .digital-signature-banner {
        margin-bottom: .6rem;
      }
    }
  }

  .anamnesis-signed {
    display: flex;
    flex-direction: column;
    gap: .6rem;
  }

  .anamnesis-signed-sheet {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    overflow: hidden;
  }

  .anamnesis-signed-heading {
    align-items: center;
    border-bottom: $border-width solid $border-color;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    padding: .6rem $layout-spacing-lg .6rem $anamnesis-signed-inset;
  }

  .anamnesis-signed-heading-icon {
    align-items: center;
    background-color: lighten($primary-color, 34%);
    border-radius: 50%;
    color: $primary-color;
    display: flex;
    flex: 0 0 1.8rem;
    font-size: $font-size-lg;
    height: 1.8rem;
    justify-content: center;
  }

  .anamnesis-signed-heading-text {
    flex: 1 1 10rem;
    min-width: 0;
  }

  .anamnesis-signed-title {
    color: $gray-color-900;
    font-size: $font-size-lg;
    font-weight: 700;
    line-height: 1.25;
  }

  .anamnesis-signed-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: .1rem;
  }

  .anamnesis-signed-date {
    align-items: center;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 600;
    gap: .3rem;
    line-height: 1;
    margin-left: auto;
    padding: .3rem .6rem;
    white-space: nowrap;
  }

  .anamnesis-signed-body {
    overflow-wrap: anywhere;
    padding: .8rem $layout-spacing-lg .8rem $anamnesis-signed-inset;
  }

  .anamnesis-signed-none {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    text-align: center;
  }

  .anamnesis-signed-pills {
    display: flex;
    flex-wrap: wrap;
    gap: .4rem;
    padding: .7rem $layout-spacing-lg .7rem $anamnesis-signed-inset;
  }

  .anamnesis-signed-pill {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-900;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 600;
    gap: .3rem;
    line-height: 1;
    padding: .3rem .6rem;
    white-space: nowrap;
    &.is-code {
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
    }
  }

  .anamnesis-signed-pill-key {
    color: $gray-color-dark;
    font-weight: 400;
  }

  .anamnesis-signed-list {
    padding: .2rem 0;
  }

  .anamnesis-signed-cid {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    padding: .6rem $layout-spacing-lg .6rem $anamnesis-signed-inset;
    position: relative;
    & + .anamnesis-signed-cid::before {
      border-top: $border-width solid $border-color;
      content: "";
      left: $anamnesis-signed-inset;
      position: absolute;
      right: $layout-spacing-lg;
      top: 0;
    }
  }

  .anamnesis-signed-cid-name {
    color: $gray-color-900;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: $size-sm) {
    .anamnesis-signature.is-signed {
      padding: $layout-spacing;
    }
    .anamnesis-signed-heading-text {
      flex-basis: calc(100% - 2.2rem);
    }
    .anamnesis-signed-date {
      margin-left: 2.2rem;
    }
  }
}
</style>
